import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faTriangleExclamation, faXmark } from '@fortawesome/pro-light-svg-icons';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../../subscription/services/auth/auth.service';
import { OrgService } from '../services/org.service';
import { OrgEntry } from '../models/org.model';
import { ConfirmService } from '../../../core/confirmation/confirm.service';
import { apiErrorMessage } from '../../../core/services/api-error';

/**
 * Top-bar notification indicator: a triangle with a count badge for anything
 * pending the current user's action. Today that's org invites and external
 * collaborator (org-admin) grants — both surface on user.orgs as
 * status='invited'. Clicking opens a dropdown with inline accept/decline.
 */
@Component({
  selector: 'app-pending-notifications',
  standalone: true,
  imports: [FontAwesomeModule, NgbDropdownModule],
  templateUrl: './pending-notifications.component.html',
  styleUrl: './pending-notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PendingNotificationsComponent {
  private orgService = inject(OrgService);
  private confirmService = inject(ConfirmService);
  private authService = inject(AuthService);

  private user = toSignal(this.authService.$user);

  pending = computed<OrgEntry[]>(() =>
    (this.user()?.orgs ?? []).filter((o) => o.status === 'invited'),
  );
  count = computed(() => this.pending().length);

  /** Per-item "in flight" set keyed by org_id (unique per pending row). */
  busy = signal<Set<string>>(new Set());

  icons = {
    alert: faTriangleExclamation,
    accept: faCheck,
    decline: faXmark,
  };

  isCollaborator(item: OrgEntry): boolean {
    return item.role === 'collaborator' && !!item.grant_id;
  }

  isBusy(item: OrgEntry): boolean {
    return this.busy().has(item.org_id);
  }

  async accept(item: OrgEntry): Promise<void> {
    this.markBusy(item.org_id, true);
    try {
      if (this.isCollaborator(item)) {
        await this.orgService.acceptCollaboratorGrant(item.grant_id!);
      } else {
        await this.orgService.acceptInvite(item.org_id);
      }
    } catch (err) {
      alert(apiErrorMessage(err, 'Failed to accept'));
    } finally {
      this.markBusy(item.org_id, false);
    }
  }

  async decline(item: OrgEntry): Promise<void> {
    const label = this.isCollaborator(item) ? 'collaborator access to' : 'invite to';
    const ok = await this.confirmService.confirm({
      title: 'Decline',
      message: `Decline ${label} ${item.name}?`,
      confirmLabel: 'Decline',
    });
    if (!ok) return;
    this.markBusy(item.org_id, true);
    try {
      if (this.isCollaborator(item)) {
        await this.orgService.declineCollaboratorGrant(item.grant_id!);
      } else {
        await this.orgService.declineInvite(item.org_id);
      }
    } catch (err) {
      alert(apiErrorMessage(err, 'Failed to decline'));
    } finally {
      this.markBusy(item.org_id, false);
    }
  }

  private markBusy(orgId: string, busy: boolean): void {
    this.busy.update((s) => {
      const next = new Set(s);
      if (busy) next.add(orgId);
      else next.delete(orgId);
      return next;
    });
  }
}
